/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Build 91 P1-8 — instant offline banner. The user reported (Build 90
// M01) that airplane-mode launches "search for network for a long time
// before showing 'Could not connect.'" — that's because the request-
// level timeouts had to fire for the UI to react. navigator.onLine
// + the online/offline events surface the state synchronously, so
// the banner appears the moment the OS reports no network instead
// of waiting for the first request to hang. @capacitor/network would
// be slightly more reliable but isn't installed, and the polish
// budget for this batch doesn't justify a new native plugin install
// + cap:sync round-trip for a banner — onLine is good enough.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { WifiOff } from "lucide-react";

export default function OfflineBanner() {
  const { t } = useTranslation();
  // SSR-safe seed: assume online when navigator is unavailable so
  // the banner doesn't flash during the brief pre-hydration window.
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 inset-x-0 z-[90] bg-destructive text-destructive-foreground px-4 py-2 text-xs font-medium flex items-center justify-center gap-2 shadow-lg"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)" }}
    >
      <WifiOff size={14} />
      <span>{t("network.offlineBanner")}</span>
    </div>
  );
}
